/**
 * Dialog to bulk create and send organizers to multiple clients.
 * POST /api/organizers/bulk-send/
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import api from '../api';

const OrganizerBulkSendDialog = ({ open, onClose, clients = [], organizerTypes = [], onSuccess }) => {
  const [organizerTypeId, setOrganizerTypeId] = useState('');
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [message, setMessage] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedClientIds([]);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const handleToggleClient = (clientId) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClientIds.length === clients.length) {
      setSelectedClientIds([]);
    } else {
      setSelectedClientIds(clients.map((c) => c.id));
    }
  };

  const handleSubmit = async () => {
    if (!organizerTypeId || selectedClientIds.length === 0) {
      setError('Please select organizer type and at least one client.');
      return;
    }
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        organizer_type_id: parseInt(organizerTypeId, 10),
        tax_year: parseInt(taxYear, 10) || new Date().getFullYear(),
        client_ids: selectedClientIds,
        message: message || undefined,
        due_date: dueDate || undefined,
      };
      const response = await api.post('/api/organizers/bulk-send/', payload);
      if (response.data?.success) {
        setResult(response.data);
        if (onSuccess) onSuccess();
      } else {
        setError(response.data?.error || 'Bulk send failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to bulk send organizers');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Send Organizers</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {result.message}
            {result.data?.created?.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Sent to {result.data.created.length} client(s).
              </Typography>
            )}
            {result.data?.errors?.length > 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {result.data.errors.length} client(s) failed.
              </Typography>
            )}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Organizer Type</InputLabel>
            <Select
              value={organizerTypeId}
              label="Organizer Type"
              onChange={(e) => setOrganizerTypeId(e.target.value)}
            >
              {organizerTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Tax Year"
            type="number"
            value={taxYear}
            onChange={(e) => setTaxYear(e.target.value)}
            inputProps={{ min: 2020, max: 2030 }}
          />
          <TextField
            size="small"
            label="Due Date (optional)"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Personal Message (optional)"
            multiline
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a note to include in the email"
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Clients ({selectedClientIds.length} selected)
            </Typography>
            <Button size="small" onClick={handleSelectAll} sx={{ textTransform: 'none', mb: 1 }}>
              {selectedClientIds.length === clients.length ? 'Deselect All' : 'Select All'}
            </Button>
            <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {clients.map((c) => (
                <ListItem key={c.id} dense>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedClientIds.includes(c.id)}
                        onChange={() => handleToggleClient(c.id)}
                        size="small"
                      />
                    }
                    label={c.display_name || c.name || `Client ${c.id}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={sending || !organizerTypeId || selectedClientIds.length === 0}
            startIcon={sending ? <CircularProgress size={16} /> : null}
          >
            {sending ? 'Sending...' : `Send to ${selectedClientIds.length} Client(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrganizerBulkSendDialog;
